// Reads + interprets the customer session for the dashboard layer.
// Single source of truth for all three view states: visitor / new / active.
import { useEffect, useState } from "react";
import { API_BASE } from "./backendUrl";

const KEY = "current_customer";
// 2026-08-21 — persistent-session fix: this is now just a client-side
// freshness cap on the sessionStorage CACHE, not the real security
// boundary. The actual session lives server-side (httpOnly cookie,
// 30-day absolute expiry, revocable on logout — see session_store.py /
// GET+POST /api/auth/session|logout). Set to the same 30 days so an
// open tab never falls out of sync with a still-valid server session.
const SESSION_HOURS = 24 * 30;
const MS_PER_HOUR = 60 * 60 * 1000;

// Same-tab auth signal. The browser `storage` event only fires in OTHER
// tabs, never in the tab that wrote sessionStorage. Without this, after
// login writes the session and SPA-navigates to /executive, the cached
// customer in `useCurrentCustomer` / CustomerDataProvider stays stale
// (visitor) and the route guard bounces back to /login — the "need 2-3
// attempts" bug. Every writer dispatches this event so all readers re-sync
// synchronously in the same tab.
export const AUTH_EVENT = "core:auth-changed";

function emitAuthChanged() {
  try { window.dispatchEvent(new Event(AUTH_EVENT)); } catch (_e) { /* ignore */ }
}

/** Persist the customer session (same tab) and notify all readers immediately. */
export function setCurrentCustomer(obj) {
  try {
    const enriched = { ...(obj || {}), stored_at: obj?.stored_at || Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(enriched));
    emitAuthChanged();
    return enriched;
  } catch (_e) {
    return obj;
  }
}

/** Best-effort parse; returns null on any error. */
function readRaw() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw || raw === "null" || raw === "undefined") return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    return obj;
  } catch (_e) {
    return null;
  }
}

/** Returns the customer object if still valid, else null. Expires the
 *  sessionStorage entry in place if `stored_at` is older than the cache cap. */
export function getCurrentCustomer() {
  const obj = readRaw();
  if (!obj) return null;
  const storedAt = Number(obj.stored_at || 0);
  if (storedAt && Date.now() - storedAt > SESSION_HOURS * MS_PER_HOUR) {
    try { sessionStorage.removeItem(KEY); } catch (_e) { /* ignore */ }
    return null;
  }
  return obj;
}

/**
 * Silent session restore — asks the backend to validate the httpOnly
 * `session_token` cookie (30-day persistent login, survives browser
 * close/reopen). Rehydrates sessionStorage on success so every existing
 * page/hook that reads getCurrentCustomer() keeps working unchanged.
 *
 * 2026-09 RCA fix — returns a tri-state result instead of null, so callers
 * can tell "definitely not authenticated" apart from "couldn't check right
 * now" (network error / backend 5xx / the /auth/session endpoint's own
 * explicit 503 for a transient Supabase failure). A temporary backend
 * hiccup must NEVER be treated the same as a real logout:
 *   { status: "ok", customer }        — session confirmed, customer refreshed
 *   { status: "unauthenticated" }     — backend explicitly said not logged in
 *   { status: "unknown" }             — couldn't tell (network/5xx) — caller
 *                                        must leave any existing session alone
 */
export async function restoreSession() {
  try {
    const res = await fetch(`${API_BASE}/auth/session`, { credentials: "include" });
    if (!res.ok) return { status: "unknown" }; // 503/500/502 etc — transient, not a logout signal
    const data = await res.json();
    if (data?.authenticated && data.customer) {
      return { status: "ok", customer: setCurrentCustomer(data.customer) };
    }
    return { status: "unauthenticated" };
  } catch (_e) {
    return { status: "unknown" }; // offline / backend unreachable — never treat as logged out
  }
}

/** "visitor" | "new" | "active" */
export function getCustomerState(c) {
  if (!c) return "visitor";
  if (c.status === "active") return "active";
  if (c.status === "profile_complete" || c.status === "pending_setup") return "new";
  // For other statuses (pending_otp, verified) treat as new — they shouldn't
  // see the executive dashboard with full chrome until activated.
  return "new";
}

/** Hook — re-reads on window focus + storage events so other tabs stay in sync. */
export function useCurrentCustomer() {
  const [cust, setCust] = useState(() => getCurrentCustomer());

  useEffect(() => {
    const refresh = () => setCust(getCurrentCustomer());
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    // Same-tab login/logout signal — fires synchronously on setCurrentCustomer
    // / clearCustomer so the session is picked up on the FIRST attempt without
    // waiting for a focus event or the 60s poll.
    window.addEventListener(AUTH_EVENT, refresh);
    // Cross-tab logout detection: sessionStorage is tab-scoped (no native
    // `storage` event fires for it across tabs), but the session cookie
    // IS shared browser-wide. So instead of just re-reading the local
    // cache, periodically re-validate against the server — if another
    // tab logged out (cookie cleared + server session revoked), this tab
    // notices within ~60s and clears its own cached state too.
    const revalidate = async () => {
      if (!getCurrentCustomer()) { setCust(null); return; }
      const result = await restoreSession();
      // 2026-09 RCA fix: only clear a cached session on an EXPLICIT
      // "unauthenticated" answer from the backend. "unknown" (network
      // error / backend 5xx / transient Supabase failure) must leave the
      // existing session exactly as-is — this was the root cause of users
      // being abruptly, silently logged out during a brief backend hiccup.
      if (result.status === "unauthenticated") { clearCustomer(); setCust(null); }
      else if (result.status === "ok") { setCust(result.customer); }
      // "unknown" — no-op, keep current cached customer, try again in 60s.
    };
    const id = setInterval(revalidate, 60 * 1000);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener(AUTH_EVENT, refresh);
      clearInterval(id);
    };
  }, []);

  return { customer: cust, state: getCustomerState(cust) };
}

/** Explicit logout — revoke the persistent session server-side, then
 *  clear the local cache. Reopening the browser afterwards must NOT
 *  restore the session, so the server call happens first/best-effort. */
export async function logout() {
  try {
    await fetch(`${API_BASE}/auth/logout`, { method: "POST", credentials: "include" });
  } catch (_e) { /* best-effort — still clear local state below */ }
  clearCustomer();
}

export function clearCustomer() {
  try { sessionStorage.removeItem(KEY); } catch (_e) { /* ignore */ }
  emitAuthChanged();
}

/**
 * 2026-11 — call after any response from the 4 session-cookie-gated
 * endpoints (dashboard-data / executive-summary / chat / ai-insights).
 * If the SPA believes a customer is logged in (sessionStorage) but the
 * response has no `customer_id` (the backend's session-cookie check found
 * no valid session — e.g. the cookie expired or was cleared in another
 * tab, without this tab's sessionStorage knowing), the two are out of
 * sync. Without this, the page would silently keep rendering the generic
 * demo/visitor data under the stale customer's name. Treat it as a logout.
 */
export function checkSessionDrift(responseCustomerId) {
  if (responseCustomerId) return;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw && raw !== "null" && raw !== "undefined") clearCustomer();
  } catch (_e) { /* ignore */ }
}

/** Derive 2-letter initials from a full name. Falls back to "C". */
export function initialsOf(name) {
  const n = (name || "").trim();
  if (!n) return "C";
  const parts = n.split(/\s+/);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (a + b).toUpperCase() || "C";
}
